import type { VendorType } from "@/interfaces";
import vendorService from "@/services/vendorService";
import { useUserStore } from "@/stores/userStore";
import { COUNTRY_BANK_FIELDS, type AllowedCountry } from "@/utils/countryConstants";
import { formatCompanyDate } from "@/utils/dateUtils";
import { Button, Modal, Spin, Tag, message } from "antd";
import type { ReactNode } from "react";
import { Fragment, useState } from "react";
import { Briefcase, Calendar, CheckCircle2, StickyNote, XCircle } from "lucide-react";

const INDIA_DOCUMENT_FIELDS = [
  { type: "panCard", label: "PAN Card", fileField: "panCardFile", fileNameField: "panCardFileName", numberField: { key: "panNumber", label: "PAN Number" } },
  { type: "gstCertificate", label: "GST Certificate", fileField: "gstCertificateFile", fileNameField: "gstCertificateFileName", numberField: { key: "gstNumber", label: "GST Number" } },
  { type: "cancelledCheque", label: "Cancelled Cheque", fileField: "cancelledChequeFile", fileNameField: "cancelledChequeFileName" },
  { type: "msmeCertificate", label: "MSME Certificate", fileField: "msmeCertificateFile", fileNameField: "msmeCertificateFileName" },
];

const US_DOCUMENT_FIELDS = [
  { type: "w9", label: "W-9", fileField: "w9File", fileNameField: "w9FileName" },
  { type: "voidedCheque", label: "Voided Cheque", fileField: "voidedChequeFile", fileNameField: "voidedChequeFileName" },
  {
    type: "certificateOfIncorporation",
    label: "Certificate of Incorporation",
    fileField: "certificateOfIncorporationFile",
    fileNameField: "certificateOfIncorporationFileName",
  },
  { type: "smallBusinessCert", label: "Small Business Certification", fileField: "smallBusinessCertFile", fileNameField: "smallBusinessCertFileName" },
];

const getDocumentCountryMode = (country?: string | null): "india" | "us" | "other" => {
  if (country === "United States") return "us";
  if (!country || country === "India") return "india";
  return "other";
};

interface ViewVendorModalProps {
  open: boolean;
  vendor: VendorType | null;
  onCancel: () => void;
  showCompanies?: boolean;
  showAssociatedUsers?: boolean;
  showContactDetails?: boolean;
}

export default function ViewVendorModal({
  open,
  vendor,
  onCancel,
  showCompanies = true,
  showAssociatedUsers = true,
  showContactDetails = true,
}: ViewVendorModalProps) {
  const dateFormat = useUserStore((state) => state.currentCompany?.dateFormat);
  const [documentLoading, setDocumentLoading] = useState<Record<string, boolean>>({});

  const handleDocumentClick = async (vendorId: string, type: string) => {
    if (documentLoading[type]) return;
    setDocumentLoading((prev) => ({ ...prev, [type]: true }));
    try {
      const res = await vendorService.getVendorDocumentUrl(vendorId, type);
      if (res?.data?.url) {
        window.open(res.data.url, "_blank", "noopener,noreferrer");
      } else {
        message.error("Could not get file link");
      }
    } catch (err) {
      message.error("Failed to fetch file link");
    } finally {
      setDocumentLoading((prev) => ({ ...prev, [type]: false }));
    }
  };

  const handleCustomDocumentClick = async (vendorId: string, docId: string) => {
    const loadingKey = `custom-${docId}`;
    if (documentLoading[loadingKey]) return;
    setDocumentLoading((prev) => ({ ...prev, [loadingKey]: true }));
    try {
      const res = await vendorService.getVendorCustomDocumentUrl(vendorId, docId);
      if (res?.data?.url) {
        window.open(res.data.url, "_blank", "noopener,noreferrer");
      } else {
        message.error("Could not get file link");
      }
    } catch (err) {
      message.error("Failed to fetch file link");
    } finally {
      setDocumentLoading((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  const Field = ({ label, value }: { label: string; value: ReactNode }) => (
    <div>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{label}</p>
      <div className="text-sm text-gray-900">{value || "—"}</div>
    </div>
  );

  return (
    <Modal
      title={
        <div className="mb-4">
          <span style={{ fontSize: 24, fontWeight: 700 }}>Vendor Details</span>
        </div>
      }
      centered
      open={open}
      onCancel={onCancel}
      width={600}
      footer={null}
    >
      {vendor && (
        <div className="space-y-6">
          <div
            className={`-mx-6 -mt-2 mb-6 px-6 py-4 ${vendor.isActive === true ? "bg-green-50 border-b border-green-100" : "bg-gray-50 border-b border-gray-200"
              }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {vendor.isActive === true ? (
                  <CheckCircle2 size={20} className="text-green-600" />
                ) : (
                  <XCircle size={20} className="text-gray-400" />
                )}
                <span className="text-sm font-semibold text-gray-900">
                  {vendor.isActive === true ? "Active Vendor" : "Inactive Vendor"}
                </span>
              </div>
              {vendor.category && <Tag color="blue">{vendor.category}</Tag>}
            </div>
          </div>

          <div className="bg-white rounded-lg">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-100">
              Basic Information
            </h3>
            <div className="grid grid-cols-2 gap-6">
              <Field label="Vendor Name" value={<span className="text-base font-semibold">{vendor.vendorName}</span>} />
              <Field label="Domain" value={vendor.domain || "—"} />
              <Field
                label="Category"
                value={
                  vendor.category ? (
                    <div className="flex items-center gap-2">
                      <Briefcase size={14} className="text-gray-400" />
                      {vendor.category}
                    </div>
                  ) : (
                    "—"
                  )
                }
              />
              <Field
                label="Vendor Type"
                value={
                  vendor.vendorType ? (
                    <Tag color={vendor.vendorType === "purchase" ? "blue" : "orange"}>
                      {vendor.vendorType.charAt(0).toUpperCase() + vendor.vendorType.slice(1)}
                    </Tag>
                  ) : (
                    "—"
                  )
                }
              />
              <Field
                label="Entity Type"
                value={vendor.type ? vendor.type.charAt(0).toUpperCase() + vendor.type.slice(1) : "—"}
              />
              <Field label="Vendor Legal Name" value={vendor.vendorLegalName || "—"} />
              {vendor.type === "individual" && (
                <Field
                  label="Contact Person"
                  value={`${vendor.title || ""} ${vendor.firstName || ""} ${vendor.lastName || ""}`.trim() || "—"}
                />
              )}
              <Field label="Currency" value={vendor.currency || "—"} />
              <Field label="Payment Terms" value={vendor.paymentTerms || "—"} />
              {showCompanies ? (
                <Field
                  label="Companies"
                  value={
                    vendor.companies && vendor.companies.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {vendor.companies.map((company) => (
                          <Tag key={company.id}>{company.companyName}</Tag>
                        ))}
                      </div>
                    ) : (
                      "—"
                    )
                  }
                />
              ) : null}
            </div>
          </div>

          {/* Communication & Integration */}
          <div className="bg-white rounded-lg">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-100">
              Communication & Integration
            </h3>
            <div className="grid grid-cols-2 gap-6">
              {showContactDetails && (
                <>
                  <Field label="Email" value={vendor.email || "—"} />
                  <Field label="Phone" value={vendor.phone || "—"} />
                  <Field
                    label="Microsoft Teams ID"
                    value={
                      vendor.teamsId ? (
                        <a
                          onClick={(e) => {
                            e.preventDefault();
                            navigator.clipboard.writeText(vendor.teamsId!);
                            message.success("Teams ID copied to clipboard");
                          }}
                          className="text-blue-600 hover:underline cursor-pointer"
                        >
                          {vendor.teamsId}
                        </a>
                      ) : (
                        "—"
                      )
                    }
                  />
                  <Field
                    label="Skype ID"
                    value={
                      vendor.skypeId ? (
                        <a
                          onClick={(e) => {
                            e.preventDefault();
                            navigator.clipboard.writeText(vendor.skypeId!);
                            message.success("Skype ID copied to clipboard");
                          }}
                          className="text-blue-600 hover:underline cursor-pointer"
                        >
                          {vendor.skypeId}
                        </a>
                      ) : (
                        "—"
                      )
                    }
                  />
                </>
              )}
              <Field
                label="LinkedIn URL"
                value={
                  vendor.linkedinUrl ? (
                    <a
                      href={vendor.linkedinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline flex items-center gap-1"
                    >
                      {vendor.linkedinUrl}
                    </a>
                  ) : (
                    "—"
                  )
                }
              />
              {showContactDetails && (
                <Field
                  label="Other Integration ID"
                  value={
                    vendor.otherIntegrationId ? (
                      <a
                        onClick={(e) => {
                          e.preventDefault();
                          navigator.clipboard.writeText(vendor.otherIntegrationId!);
                          message.success("Integration ID copied to clipboard");
                        }}
                        className="text-blue-600 hover:underline cursor-pointer"
                      >
                        {vendor.otherIntegrationId}
                      </a>
                    ) : (
                      "—"
                    )
                  }
                />
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-100">
              Additional Information
            </h3>
            <div className="grid grid-cols-2 gap-6">
              <Field
                label="Join Date"
                value={
                  vendor.createdAt ? (
                    <div className="flex items-center gap-2">
                      <Calendar size={14} className="text-gray-400" />
                      {formatCompanyDate(vendor.createdAt, dateFormat)}
                    </div>
                  ) : (
                    "—"
                  )
                }
              />
              <Field
                label="Last Updated At"
                value={
                  vendor.updatedAt ? (
                    <div className="flex items-center gap-2">
                      <Calendar size={14} className="text-gray-400" />
                      {formatCompanyDate(vendor.updatedAt, dateFormat)}
                    </div>
                  ) : (
                    "—"
                  )
                }
              />
              <Field label="Country" value={vendor.country || "—"} />
              <Field label="Timezone" value={vendor.timezone || "—"} />
              {vendor.addressLine1 ? (
                <>
                  <Field label="Address Line 1" value={vendor.addressLine1 || "—"} />
                  <Field label="Address Line 2" value={vendor.addressLine2 || "—"} />
                  <Field label="City" value={vendor.city || "—"} />
                  <Field label="State" value={vendor.state || "—"} />
                  <Field label="Pin" value={vendor.pin || "—"} />
                </>
              ) : (
                <Field label="Address" value={vendor.address || "—"} />
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-100">Documents</h3>
            {getDocumentCountryMode(vendor.country) !== "other" && (
              <div className="grid grid-cols-2 gap-6">
                {(getDocumentCountryMode(vendor.country) === "us" ? US_DOCUMENT_FIELDS : INDIA_DOCUMENT_FIELDS).map((doc) => {
                  const fileKey = (vendor as any)[doc.fileField] as string | null | undefined;
                  const fileName = (vendor as any)[doc.fileNameField] as string | null | undefined;
                  const numberField = (doc as any).numberField as { key: string; label: string } | undefined;
                  return (
                    <Fragment key={doc.type}>
                      <Field
                        label={doc.label}
                        value={
                          fileKey ? (
                            <>
                              <span
                                className="text-blue-600 hover:underline cursor-pointer"
                                onClick={() => handleDocumentClick(vendor.id, doc.type)}
                                title="Click to view (link expires in 1 hour)"
                              >
                                {fileName || fileKey.split("/").pop() || "View File"}
                              </span>
                              {documentLoading[doc.type] && <Spin size="small" className="ml-2" />}
                            </>
                          ) : (
                            "—"
                          )
                        }
                      />
                      {numberField && (
                        <Field label={numberField.label} value={(vendor as any)[numberField.key] || "—"} />
                      )}
                    </Fragment>
                  );
                })}
              </div>
            )}
            {vendor.customDocuments && vendor.customDocuments.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-medium text-gray-500 mb-2">Custom Documents</p>
                <div className="grid grid-cols-2 gap-6">
                  {vendor.customDocuments.map((doc) => (
                    <Field
                      key={doc.id}
                      label={doc.name}
                      value={
                        <>
                          <span
                            className="text-blue-600 hover:underline cursor-pointer"
                            onClick={() => handleCustomDocumentClick(vendor.id, doc.id)}
                            title="Click to view (link expires in 1 hour)"
                          >
                            {doc.fileName || "View File"}
                          </span>
                          {documentLoading[`custom-${doc.id}`] && <Spin size="small" className="ml-2" />}
                        </>
                      }
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-100">Bank Details</h3>
            <div className="grid grid-cols-2 gap-6">
              <Field label="Bank Name" value={vendor.bankName || "—"} />
              <Field label="Account Number" value={vendor.accountNumber || "—"} />
              <Field label="Account Holder Name" value={vendor.accountHolderName || "—"} />
              <Field label="IFSC Code" value={vendor.ifscCode || "—"} />
              <Field label="Account Type" value={vendor.accountType || "—"} />
              <Field label="Bank Detail Notes" value={vendor.bankDetailNotes || "—"} />
              {(() => {
                const country = vendor.country as AllowedCountry;
                const metadata = vendor.bankMetadata || {};
                if (country && COUNTRY_BANK_FIELDS[country]) {
                  return COUNTRY_BANK_FIELDS[country].map((field) => (
                    <Field key={field.key} label={field.label} value={metadata[field.key] || "—"} />
                  ));
                }
                if (metadata && Object.keys(metadata).length > 0) {
                  return Object.entries(metadata).map(([key, value]) => (
                    <Field key={key} label={key} value={value || "—"} />
                  ));
                }
                return null;
              })()}
            </div>
          </div>

          {vendor.description ? (
            <div className="bg-white rounded-lg">
              <h3 className="text-sm font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-100 flex items-center gap-2">
                <StickyNote size={14} className="text-gray-400" />
                Description
              </h3>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{vendor.description}</p>
            </div>
          ) : null}

          {showAssociatedUsers ? (
            vendor.users && vendor.users.length > 0 ? (
              <div className="bg-white rounded-lg">
                <h3 className="text-sm font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-100">
                  Associated Users
                </h3>
                <div className="space-y-3">
                  {vendor.users.map((user, index) => (
                    <div key={user.id || index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {`${user.firstName || ""} ${user.lastName || ""}`.trim() || "No name"}
                        </p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                      </div>
                      {user.phone ? <p className="text-xs text-gray-600">{user.phone}</p> : null}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg">
                <h3 className="text-sm font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-100">
                  Associated Users
                </h3>
                <p className="text-sm text-gray-500 italic">No users associated with this vendor</p>
              </div>
            )
          ) : null}

          <div className="flex justify-end gap-2">
            <Button onClick={onCancel}>Close</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
